import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const TRUSS_SIZE = 0.4;
const SPOT_SPACING = 2.5;
const ROW_PITCH = 0.9;
const SEAT_PITCH = 0.55;
const AISLE_WIDTH = 1.2;
/** Largeur au-delà de laquelle un bloc de chaises est coupé par une allée. */
const MAX_BLOCK_WIDTH = 14;
const WALL_THICKNESS = 0.3;

export type StageStyle = "concert" | "dance" | "talk";

/** Hauteur de l'estrade et du mur de fond, selon l'usage de la scène. */
const STAGE_PROPORTIONS: Record<StageStyle, { deck: number; wall: number }> = {
  concert: { deck: 1, wall: 7 },
  dance: { deck: 0.6, wall: 6 },
  talk: { deck: 0.5, wall: 4.5 },
};

const deck = new THREE.MeshStandardMaterial({ name: "deck", color: "#46464f", roughness: 0.85, metalness: 0 });
const floor = new THREE.MeshStandardMaterial({ name: "floor", color: "#34343c", roughness: 0.4, metalness: 0.1 });
const danceFloor = new THREE.MeshStandardMaterial({ name: "dance-floor", color: "#d8b48a", roughness: 0.25, metalness: 0 });
const carpet = new THREE.MeshStandardMaterial({ name: "carpet", color: "#3a2a3e", roughness: 0.95, metalness: 0 });
const wall = new THREE.MeshStandardMaterial({ name: "wall", color: "#2a2a44", roughness: 0.9, metalness: 0 });
const velvet = new THREE.MeshStandardMaterial({ name: "velvet", color: "#151519", roughness: 1, metalness: 0 });
const curtain = new THREE.MeshStandardMaterial({ name: "curtain", color: "#5a1a2c", roughness: 1, metalness: 0 });
const frame = new THREE.MeshStandardMaterial({ name: "frame", color: "#0b0b0d", roughness: 0.6, metalness: 0.1 });
const truss = new THREE.MeshStandardMaterial({ name: "truss", color: "#b9bec6", roughness: 0.4, metalness: 0.7 });
const speaker = new THREE.MeshStandardMaterial({ name: "speaker", color: "#111114", roughness: 0.7, metalness: 0.1 });
const chrome = new THREE.MeshStandardMaterial({ name: "chrome", color: "#c8ccd2", roughness: 0.25, metalness: 0.9, side: THREE.DoubleSide });
const cymbal = new THREE.MeshStandardMaterial({ name: "cymbal", color: "#d4a640", roughness: 0.3, metalness: 0.8 });
const drumShell = new THREE.MeshStandardMaterial({ name: "drum", color: "#b3152b", roughness: 0.4, metalness: 0.2 });
const drumHead = new THREE.MeshStandardMaterial({ name: "drum-head", color: "#ece8de", roughness: 0.8, metalness: 0 });
const wood = new THREE.MeshStandardMaterial({ name: "wood", color: "#7a5236", roughness: 0.7, metalness: 0 });
const cushion = new THREE.MeshStandardMaterial({ name: "cushion", color: "#e8e2d6", roughness: 0.9, metalness: 0 });
const leaves = new THREE.MeshStandardMaterial({ name: "leaves", color: "#3f7d45", roughness: 0.9, metalness: 0 });
const ledStrip = new THREE.MeshBasicMaterial({ name: "led", color: "#e6007e" });
const spotWarm = new THREE.MeshBasicMaterial({ name: "spot", color: "#fff1c9" });
const spotMagenta = new THREE.MeshBasicMaterial({ name: "spot-magenta", color: "#ff4fd8" });
const spotCyan = new THREE.MeshBasicMaterial({ name: "spot-cyan", color: "#4fe3ff" });
const seatFabric = new THREE.MeshStandardMaterial({ name: "seat", color: "#b3152b", roughness: 0.9, metalness: 0 });
const seatFrame = new THREE.MeshStandardMaterial({ name: "seat-frame", color: "#2b2b2f", roughness: 0.6, metalness: 0.4 });

function box(width: number, height: number, depth: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  geometry.translate(x, y, z);
  return geometry;
}

/** Cylindre debout ; `lying` le couche le long de z, comme une grosse caisse face au public. */
function cylinder(radius: number, height: number, x: number, y: number, z: number, lying = false): THREE.BufferGeometry {
  const geometry = new THREE.CylinderGeometry(radius, radius, height, 16);
  if (lying) geometry.rotateX(Math.PI / 2);
  geometry.translate(x, y, z);
  return geometry;
}

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

/** Positions régulières de `from` à `to`, extrémités comprises, espacées d'au plus `spacing`. */
function spaced(from: number, to: number, spacing: number): number[] {
  const count = Math.max(1, Math.ceil(Math.abs(to - from) / spacing));
  return Array.from({ length: count + 1 }, (_, i) => from + ((to - from) * i) / count);
}

export interface StageOptions {
  /** Concert par défaut. */
  style?: StageStyle;
  /** Affichée sur l'écran du fond, sans déformation. */
  image?: string;
  /** Appelé quand l'image est chargée : la scène est à redessiner. */
  onImageLoaded?: () => void;
}

interface StageFrame {
  root: THREE.Group;
  width: number;
  length: number;
  back: number;
  front: number;
  deckTop: number;
  wallHeight: number;
}

/**
 * Scène à la largeur et à la profondeur voulues : estrade, mur de fond portant un écran, puis
 * l'aménagement de son usage — concert, danse ou conférence. Le public est côté +z ; mesures en mètres.
 */
export function buildStage(width: number, length: number, options: StageOptions = {}): THREE.Group {
  const style = options.style ?? "concert";
  const { deck: deckTop, wall: wallHeight } = STAGE_PROPORTIONS[style];
  const stage: StageFrame = { root: new THREE.Group(), width, length, back: -length / 2, front: length / 2, deckTop, wallHeight };
  const { root, back, front } = stage;

  const surface = style === "dance" ? danceFloor : style === "talk" ? carpet : floor;
  root.add(merged([box(width, deckTop - 0.04, length, 0, (deckTop - 0.04) / 2, 0)], deck, "Deck"));
  root.add(merged([box(width - 0.1, 0.04, length - 0.1, 0, deckTop - 0.02, 0)], surface, "Floor"));
  root.add(merged([box(width, 0.15, 0.04, 0, deckTop * 0.55, front + 0.02)], ledStrip, "Led"));
  root.add(merged([box(width, wallHeight, WALL_THICKNESS, 0, deckTop + wallHeight / 2, back + WALL_THICKNESS / 2)], style === "talk" ? curtain : wall, "Wall"));

  const screenWidth = Math.min(width * (style === "talk" ? 0.5 : 0.6), 14);
  const screenHeight = Math.min(wallHeight - (style === "talk" ? 1.6 : 1.4), screenWidth * 0.56);
  const screenY = deckTop + (style === "talk" ? 1.2 : 0.8) + screenHeight / 2;
  const screenZ = back + WALL_THICKNESS + 0.06;
  root.add(merged([box(screenWidth + 0.5, screenHeight + 0.5, 0.1, 0, screenY, screenZ - 0.01)], ledStrip, "ScreenLed"));
  root.add(merged([box(screenWidth + 0.3, screenHeight + 0.3, 0.12, 0, screenY, screenZ)], frame, "Screen"));
  if (options.image) root.add(screenImage(options.image, screenWidth, screenHeight, screenY, screenZ + 0.07, options.onImageLoaded));

  if (style === "concert") furnishConcert(stage);
  else if (style === "dance") furnishDance(stage);
  else furnishTalk(stage);
  return root;
}

/** Portique de projecteurs au bord de scène ; `lenses` alterne les couleurs d'un projecteur à l'autre. */
function addPortal({ root, width, length, back, front, deckTop, wallHeight }: StageFrame, lenses: THREE.Material[]): void {
  const top = deckTop + wallHeight + 1;
  root.add(merged([
    box(TRUSS_SIZE, top, TRUSS_SIZE, -width / 2 + TRUSS_SIZE / 2, top / 2, front - TRUSS_SIZE / 2),
    box(TRUSS_SIZE, top, TRUSS_SIZE, width / 2 - TRUSS_SIZE / 2, top / 2, front - TRUSS_SIZE / 2),
    box(TRUSS_SIZE, top, TRUSS_SIZE, -width / 2 + TRUSS_SIZE / 2, top / 2, back + TRUSS_SIZE / 2),
    box(TRUSS_SIZE, top, TRUSS_SIZE, width / 2 - TRUSS_SIZE / 2, top / 2, back + TRUSS_SIZE / 2),
    box(width, TRUSS_SIZE, TRUSS_SIZE, 0, top - TRUSS_SIZE / 2, front - TRUSS_SIZE / 2),
    box(width, TRUSS_SIZE, TRUSS_SIZE, 0, top - TRUSS_SIZE / 2, back + TRUSS_SIZE / 2),
    box(TRUSS_SIZE, TRUSS_SIZE, length, -width / 2 + TRUSS_SIZE / 2, top - TRUSS_SIZE / 2, 0),
    box(TRUSS_SIZE, TRUSS_SIZE, length, width / 2 - TRUSS_SIZE / 2, top - TRUSS_SIZE / 2, 0),
  ], truss, "Truss"));

  const spotXs = spaced(-width / 2 + 1.2, width / 2 - 1.2, SPOT_SPACING);
  const spotY = top - TRUSS_SIZE - 0.25;
  const spotZ = front - TRUSS_SIZE / 2;
  root.add(merged(spotXs.map((x) => box(0.35, 0.45, 0.35, x, spotY, spotZ)), speaker, "Spots"));
  lenses.forEach((lens, index) => {
    const xs = spotXs.filter((_, i) => i % lenses.length === index);
    if (xs.length) root.add(merged(xs.map((x) => box(0.26, 0.04, 0.26, x, spotY - 0.24, spotZ)), lens, `SpotLenses${index}`));
  });
}

/** Batterie sur son praticable, amplis, retours et colonnes de son suspendues au portique. */
function furnishConcert(stage: StageFrame): void {
  const { root, width, back, front, deckTop, wallHeight } = stage;
  addPortal(stage, [spotWarm]);

  const riserZ = back + 2.4;
  const riserTop = deckTop + 0.5;
  root.add(merged([box(3.2, 0.5, 2.6, 0, deckTop + 0.25, riserZ)], deck, "Riser"));
  addDrumKit(root, riserTop, riserZ);
  addMicStands(root, deckTop, [[-1.6, front - 2.2], [1.6, front - 2.2], [0, front - 1.6]]);

  const ampZ = back + 1.2;
  root.add(merged([
    box(0.8, 0.9, 0.45, -3, deckTop + 0.45, ampZ),
    box(0.8, 0.9, 0.45, 3, deckTop + 0.45, ampZ),
    box(0.9, 1.7, 0.7, -width / 2 + 0.9, deckTop + 0.85, front - 0.6),
    box(0.9, 1.7, 0.7, width / 2 - 0.9, deckTop + 0.85, front - 0.6),
    box(0.7, 0.35, 0.5, -1.2, deckTop + 0.17, front - 0.5),
    box(0.7, 0.35, 0.5, 1.2, deckTop + 0.17, front - 0.5),
    ...[-1, 1].flatMap((side) => Array.from({ length: 6 }, (_, i) =>
      box(1, 0.32, 0.7, side * (width / 2 + 0.7), deckTop + wallHeight - 0.2 - i * 0.36, front - 1))),
  ], speaker, "Speakers"));
}

interface Drum {
  radius: number;
  depth: number;
  x: number;
  y: number;
  z: number;
  tilt: number;
}

/** Cylindre d'axe y basculé de `tilt` autour de x, centré en (x, y, z). */
function tiltedCylinder(radiusTop: number, radiusBottom: number, height: number, tilt: number, x: number, y: number, z: number, openEnded = false): THREE.BufferGeometry {
  const geometry = new THREE.CylinderGeometry(radiusTop, radiusBottom, height, 24, 1, openEnded);
  geometry.rotateX(tilt);
  geometry.translate(x, y, z);
  return geometry;
}

/** Pied à trois branches posé en (x, z), tige comprise jusqu'à `height`. */
function tripod(x: number, floorY: number, z: number, height: number): THREE.BufferGeometry[] {
  const legLength = 0.32;
  const spread = 1;
  return [
    cylinder(0.012, height, x, floorY + height / 2, z),
    ...[0, 1, 2].map((i) => {
      const leg = new THREE.CylinderGeometry(0.01, 0.01, legLength, 6);
      leg.rotateZ(spread);
      leg.translate((legLength / 2) * Math.sin(spread), (legLength / 2) * Math.cos(spread), 0);
      leg.rotateY((i * 2 * Math.PI) / 3 + Math.PI / 6);
      leg.translate(x, floorY, z);
      return leg;
    }),
  ];
}

/**
 * Batterie tournée vers le public, posée sur le praticable dont le dessus est à `floorY` : le
 * batteur, assis côté fond, a le charleston et la caisse claire à sa gauche (+x), la tom basse et
 * la ride à sa droite.
 */
function addDrumKit(root: THREE.Group, floorY: number, z: number): void {
  const drums: Drum[] = [
    { radius: 0.28, depth: 0.42, x: 0, y: floorY + 0.3, z: z + 0.25, tilt: Math.PI / 2 },
    { radius: 0.15, depth: 0.2, x: 0.18, y: floorY + 0.8, z: z + 0.2, tilt: -0.35 },
    { radius: 0.16, depth: 0.22, x: -0.18, y: floorY + 0.81, z: z + 0.2, tilt: -0.35 },
    { radius: 0.18, depth: 0.14, x: 0.34, y: floorY + 0.62, z: z - 0.2, tilt: -0.12 },
    { radius: 0.2, depth: 0.38, x: -0.5, y: floorY + 0.46, z: z - 0.2, tilt: 0 },
  ];
  const ends = (drum: Drum, inset: number) => [-1, 1].map((side) => {
    const offset = side * (drum.depth / 2 - inset);
    return { x: drum.x, y: drum.y + offset * Math.cos(drum.tilt), z: drum.z + offset * Math.sin(drum.tilt) };
  });
  root.add(merged(drums.map((drum) =>
    tiltedCylinder(drum.radius, drum.radius, drum.depth - 0.02, drum.tilt, drum.x, drum.y, drum.z)), drumShell, "Drums"));
  root.add(merged(drums.flatMap((drum) => ends(drum, 0.006).map((end) =>
    tiltedCylinder(drum.radius * 0.98, drum.radius * 0.98, 0.012, drum.tilt, end.x, end.y, end.z))), drumHead, "DrumHeads"));

  const [bass, , , snare, floorTom] = drums as [Drum, Drum, Drum, Drum, Drum];
  const hiHat = { x: 0.7, z: z - 0.1, height: 0.86 };
  const crash = { x: 0.62, z: z + 0.42, height: 1.25 };
  const ride = { x: -0.78, z: z + 0.3, height: 1.1 };
  const seat = { z: z - 0.68, height: 0.5 };
  root.add(merged([
    ...drums.flatMap((drum) => ends(drum, 0.012).map((end) =>
      tiltedCylinder(drum.radius * 1.03, drum.radius * 1.03, 0.03, drum.tilt, end.x, end.y, end.z, true))),
    cylinder(0.02, 0.22, 0, bass.y + bass.radius + 0.1, z + 0.2),
    ...[-1, 1].map((side) => cylinder(0.012, 0.22, side * 0.26, floorY + 0.11, bass.z + bass.depth / 2 - 0.05)),
    box(0.1, 0.04, 0.26, 0, floorY + 0.02, bass.z - bass.depth / 2 - 0.13),
    ...tripod(snare.x, floorY, snare.z, snare.y - snare.depth / 2 - floorY),
    ...[0, 1, 2].map((i) => {
      const angle = (i * 2 * Math.PI) / 3;
      return cylinder(0.012, floorTom.y - floorY, floorTom.x + Math.cos(angle) * (floorTom.radius + 0.03), floorY + (floorTom.y - floorY) / 2, floorTom.z + Math.sin(angle) * (floorTom.radius + 0.03));
    }),
    ...[hiHat, crash, ride].flatMap((stand) => tripod(stand.x, floorY, stand.z, stand.height)),
    ...tripod(0, floorY, seat.z, seat.height - 0.05),
  ], chrome, "DrumHardware"));
  root.add(merged([
    tiltedCylinder(0.03, 0.18, 0.025, 0, hiHat.x, floorY + hiHat.height, hiHat.z),
    tiltedCylinder(0.03, 0.18, 0.025, Math.PI, hiHat.x, floorY + hiHat.height + 0.035, hiHat.z),
    tiltedCylinder(0.035, 0.23, 0.025, -0.25, crash.x, floorY + crash.height + 0.01, crash.z),
    tiltedCylinder(0.045, 0.28, 0.025, -0.15, ride.x, floorY + ride.height + 0.01, ride.z),
  ], cymbal, "Cymbals"));
  root.add(merged([cylinder(0.19, 0.09, 0, floorY + seat.height, seat.z)], speaker, "DrumThrone"));
}

/** Pieds de micro à hauteur de chanteur, le micro penché vers l'arrière de la scène, où se tient le musicien. */
function addMicStands(root: THREE.Group, floorY: number, positions: [number, number][]): void {
  const height = 1.45;
  const tilt = -1.1;
  const body = 0.16;
  root.add(merged(positions.flatMap(([x, z]) => tripod(x, floorY, z, height)), chrome, "MicStands"));
  root.add(merged(positions.map(([x, z]) =>
    tiltedCylinder(0.016, 0.011, body, tilt, x, floorY + height + (body / 2) * Math.cos(tilt), z + (body / 2) * Math.sin(tilt))), speaker, "Microphones"));
  root.add(merged(positions.map(([x, z]) => {
    const grille = new THREE.SphereGeometry(0.028, 12, 8);
    grille.translate(x, floorY + height + body * Math.cos(tilt), z + body * Math.sin(tilt));
    return grille;
  }), chrome, "MicGrilles"));
}

/** Plateau dégagé au sol clair, pendrillons sur les côtés, projecteurs de couleur. */
function furnishDance(stage: StageFrame): void {
  const { root, width, length, back, deckTop, wallHeight } = stage;
  addPortal(stage, [spotMagenta, spotCyan]);
  const wings = [0.3, 0.6].flatMap((share) => [-1, 1].map((side) =>
    box(1.6, wallHeight, 0.08, side * (width / 2 - 0.8), deckTop + wallHeight / 2, back + length * share)));
  root.add(merged(wings, velvet, "Wings"));
}

/** Pupitre, table et trois fauteuils, plantes de part et d'autre : la scène d'une conférence. */
function furnishTalk({ root, width, back, front, deckTop }: StageFrame): void {
  const tableZ = back + 2.6;
  const lecternX = Math.min(width / 2 - 1.5, 4.5);
  root.add(merged([
    box(0.65, 1.1, 0.5, lecternX, deckTop + 0.55, front - 1.6),
    box(0.75, 0.05, 0.6, lecternX, deckTop + 1.12, front - 1.62),
    box(2.6, 0.06, 0.8, -1, deckTop + 0.75, tableZ),
    ...[-2.2, 0.2].map((x) => box(0.06, 0.72, 0.7, x, deckTop + 0.36, tableZ)),
  ], wood, "Furniture"));
  root.add(merged([cylinder(0.01, 0.4, lecternX, deckTop + 1.3, front - 1.75)], chrome, "Microphone"));
  root.add(merged([-1.9, -1, -0.1].flatMap((x) => [
    box(0.6, 0.45, 0.6, x, deckTop + 0.225, tableZ - 0.85),
    box(0.6, 0.6, 0.12, x, deckTop + 0.75, tableZ - 1.1),
  ]), cushion, "Armchairs"));
  root.add(merged([-1, 1].map((side) => cylinder(0.25, 0.5, side * (width / 2 - 0.7), deckTop + 0.25, back + 1)), wood, "Pots"));
  root.add(merged([-1, 1].map((side) => {
    const bush = new THREE.SphereGeometry(0.55, 12, 8);
    bush.translate(side * (width / 2 - 0.7), deckTop + 1, back + 1);
    return bush;
  }), leaves, "Plants"));
}

function screenImage(src: string, width: number, height: number, y: number, z: number, onLoaded?: () => void): THREE.Mesh {
  const geometry = new THREE.PlaneGeometry(width, height);
  geometry.setDrawRange(0, 0);
  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin("anonymous");
  const texture = loader.load(src, (loaded) => {
    const image = loaded.image as { width: number; height: number };
    const ratio = image.width / image.height;
    const fit = Math.min(width / ratio, height) * 0.92;
    geometry.scale((fit * ratio) / width, fit / height, 1);
    geometry.setDrawRange(0, Infinity);
    onLoaded?.();
  }, undefined, (error) => console.warn(`[tokamap] Image d'écran de scène illisible : ${src.slice(0, 80)}`, error));
  texture.colorSpace = THREE.SRGBColorSpace;
  // Le surlignage remplace le matériau par une copie : l'image apparaît donc par la géométrie et la
  // texture, partagées, jamais par le matériau.
  const material = new THREE.MeshBasicMaterial({ name: "screen-image", transparent: true, map: texture });
  const plane = new THREE.Mesh(geometry, material);
  plane.name = "ScreenImage";
  plane.position.set(0, y, z);
  return plane;
}

/**
 * Rangées de chaises tournées vers +z, à la largeur et à la profondeur voulues, coupées par une
 * allée au-delà de 8 m, puis tous les 14 m au plus. Fusionnées en deux maillages : des centaines
 * de chaises pour le prix d'un objet.
 */
export function buildSeating(width: number, length: number): THREE.Group {
  const root = new THREE.Group();
  const blocks = width > 8 ? Math.max(2, Math.ceil((width + AISLE_WIDTH) / (MAX_BLOCK_WIDTH + AISLE_WIDTH))) : 1;
  const blockWidth = (width - (blocks - 1) * AISLE_WIDTH) / blocks;
  const columns = Array.from({ length: blocks }, (_, i) => -width / 2 + i * (blockWidth + AISLE_WIDTH))
    .flatMap((from) => seatCenters(from, from + blockWidth));
  const rows = spaced(-length / 2 + ROW_PITCH / 2, length / 2 - ROW_PITCH / 2, ROW_PITCH);

  const fabric: THREE.BufferGeometry[] = [];
  const legs: THREE.BufferGeometry[] = [];
  for (const z of rows) {
    for (const x of columns) {
      fabric.push(box(0.46, 0.08, 0.44, x, 0.45, z));
      fabric.push(box(0.46, 0.45, 0.06, x, 0.72, z - 0.2));
      legs.push(box(0.04, 0.45, 0.04, x - 0.2, 0.225, z + 0.15));
      legs.push(box(0.04, 0.45, 0.04, x + 0.2, 0.225, z + 0.15));
      legs.push(box(0.04, 0.95, 0.04, x - 0.2, 0.475, z - 0.2));
      legs.push(box(0.04, 0.95, 0.04, x + 0.2, 0.475, z - 0.2));
    }
  }
  root.add(merged(fabric, seatFabric, "Seats"));
  root.add(merged(legs, seatFrame, "SeatFrames"));
  return root;
}

function seatCenters(from: number, to: number): number[] {
  const count = Math.max(1, Math.floor((to - from) / SEAT_PITCH));
  const used = count * SEAT_PITCH;
  const start = from + (to - from - used) / 2 + SEAT_PITCH / 2;
  return Array.from({ length: count }, (_, i) => start + i * SEAT_PITCH);
}

const TABLE_PITCH_X = 1.8;
const TABLE_PITCH_Z = 1.75;
const TABLES_PER_GROUP = 2;
/** Passage entre deux groupes d'une même ligne, et allée entre deux lignes. */
const GROUP_GAP = 2;
const ROW_GAP = 2.5;

const tableWood = new THREE.MeshStandardMaterial({ name: "table-wood", color: "#a87445", roughness: 0.85, metalness: 0 });
const tableFrame = new THREE.MeshStandardMaterial({ name: "table-frame", color: "#5b3b22", roughness: 0.8, metalness: 0 });

/**
 * Tables de pique-nique à bancs intégrés, collées deux par deux bout à bout, avec un passage
 * entre les groupes et une allée entre les lignes. Fusionnées en deux maillages, comme les chaises.
 */
export function buildPicnicTables(width: number, length: number): THREE.Group {
  const root = new THREE.Group();
  const groupWidth = TABLES_PER_GROUP * TABLE_PITCH_X;
  const groups = Math.max(1, Math.floor((width + GROUP_GAP) / (groupWidth + GROUP_GAP)));
  const rows = Math.max(1, Math.floor((length + ROW_GAP) / (TABLE_PITCH_Z + ROW_GAP)));
  const usedWidth = groups * groupWidth + (groups - 1) * GROUP_GAP;
  const usedLength = rows * TABLE_PITCH_Z + (rows - 1) * ROW_GAP;
  const planks: THREE.BufferGeometry[] = [];
  const legs: THREE.BufferGeometry[] = [];
  for (let g = 0; g < groups; g++) {
    for (let t = 0; t < TABLES_PER_GROUP; t++) {
      const x = -usedWidth / 2 + g * (groupWidth + GROUP_GAP) + (t + 0.5) * TABLE_PITCH_X;
      for (let j = 0; j < rows; j++) {
        const z = -usedLength / 2 + j * (TABLE_PITCH_Z + ROW_GAP) + TABLE_PITCH_Z / 2;
        planks.push(box(1.8, 0.05, 0.75, x, 0.75, z));
        planks.push(box(1.8, 0.04, 0.28, x, 0.45, z - 0.66));
        planks.push(box(1.8, 0.04, 0.28, x, 0.45, z + 0.66));
        for (const side of [-0.7, 0.7]) {
          legs.push(box(0.06, 0.72, 0.06, x + side, 0.39, z));
          legs.push(box(0.06, 0.06, 1.6, x + side, 0.4, z));
        }
      }
    }
  }
  root.add(merged(planks, tableWood, "Planks"));
  root.add(merged(legs, tableFrame, "Legs"));
  return root;
}
